import type { TFunction } from 'i18next';

import type { TVirtuousContactMappingKey } from '@feathr/blackbox';
import {
  EVirtuousContactMappings,
  VIRTUOUS_CONTACT_DATA_TYPES,
  VIRTUOUS_REQUIRED_CONTACT_FIELDS,
} from '@feathr/blackbox';
import type { IIntegrationUIConfig } from '@feathr/components';
import {
  getCrmMappingFields,
  getCrmTabs,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmConfig.utils';

/** Where a Virtuous admin creates the API key the connect step asks for. */
export const VIRTUOUS_API_KEY_URL = 'https://connect.virtuoussoftware.com/';

/** Toggle keys. Each maps to one model attribute. */
export const CONTACT_DOWNSYNC_KEY = 'contacts';
export const GIFT_DOWNSYNC_KEY = 'gifts';
export const DONATION_UPSYNC_KEY = 'donations';
export const PERSON_UPSYNC_KEY = 'people';
export const ACTIVITY_UPSYNC_KEY = 'activity';
/** The select fields on the donation and activity toggles. */
export const PROJECT_KEY = 'project_id';
export const NOTE_TYPE_KEY = 'activity_note_type';

/** Mappings tab section keys. Contact sync renders above the mapping table, create contacts below it. */
export const CONTACT_SYNC_SECTION_KEY = 'contact_sync';
export const CREATE_CONTACTS_SECTION_KEY = 'create_contacts';

export const CONNECT_STEP_KEY = 'connect_account';

export function getVirtuousContactLabel(t: TFunction, key: TVirtuousContactMappingKey): string {
  const labels: Record<TVirtuousContactMappingKey, string> = {
    addressCity: t('City'),
    addressCountry: t('Country'),
    addressLine1: t('Address line 1'),
    addressLine2: t('Address line 2'),
    addressPostal: t('Postal code'),
    addressState: t('State / Province'),
    approximateAge: t('Age'),
    birthDate: t('Birth date'),
    contactType: t('Contact type'),
    email: t('Primary email'),
    firstName: t('First name'),
    gender: t('Gender'),
    id: t('Virtuous contact ID'),
    individualId: t('Individual ID'),
    informalName: t('Informal name'),
    isDeceased: t('Deceased'),
    lastGiftAmount: t('Last gift amount'),
    lastGiftDate: t('Last gift date'),
    lastName: t('Last name'),
    lifeToDateGiving: t('Lifetime giving'),
    middleName: t('Middle name'),
    name: t('Contact name'),
    phone: t('Primary phone'),
    prefix: t('Prefix'),
    suffix: t('Suffix'),
    tags: t('Tags'),
    yearToDateGiving: t('Year-to-date giving'),
  };
  return labels[key];
}

/** Virtuous page config. Every control is live; field upsync directions stay disabled. */
export function getVirtuousConfig(t: TFunction): IIntegrationUIConfig {
  return {
    crmName: 'Virtuous',
    mapping: {
      title: t('People'),
      crmRecordLabel: t('Contacts'),
      description: t(
        'Match Feathr people to Virtuous contacts and choose which way each one syncs.',
      ),
      fields: getCrmMappingFields({
        dataTypes: VIRTUOUS_CONTACT_DATA_TYPES,
        getLabel: (key) => getVirtuousContactLabel(t, key),
        keys: Object.values(EVirtuousContactMappings),
        requiredKeys: VIRTUOUS_REQUIRED_CONTACT_FIELDS,
        upsyncUnavailable: t('Fields sync from Virtuous to Feathr only.'),
      }),
    },
    settingsSections: [],
    setupSteps: [
      {
        key: CONNECT_STEP_KEY,
        title: t('Connect Virtuous account'),
        description: t(
          'Create an API key in Virtuous Connect, then paste it here to connect Virtuous to Feathr.',
        ),
        descriptionLink: { label: t('Virtuous Connect'), href: VIRTUOUS_API_KEY_URL },
        actionLabel: t('Connect Virtuous'),
      },
    ],
    tabs: getCrmTabs(t),
    title: t('Virtuous'),
    toggleSections: [
      {
        crmRecordLabel: t('Contacts'),
        description: t('Keep Feathr people up to date with their Virtuous contacts.'),
        heading: t('People'),
        key: CONTACT_SYNC_SECTION_KEY,
        syncRule: 'down_sync',
        tab: 'mappings',
        toggles: [
          {
            description: t(
              'When a contact is created or updated in Virtuous, the matching Feathr person is updated with the mapped fields below.',
            ),
            key: CONTACT_DOWNSYNC_KEY,
            label: t('Sync Virtuous contacts to Feathr'),
            name: 'virtuous-contact-sync-card',
          },
        ],
      },
      {
        crmRecordLabel: t('Contacts'),
        description: t(
          'When Feathr captures a new person, create them as a new contact in Virtuous. Virtuous matches them to an existing contact when it imports them, and existing contacts are never updated.',
        ),
        heading: t('People'),
        key: CREATE_CONTACTS_SECTION_KEY,
        syncRule: 'up_sync',
        tab: 'mappings',
        toggles: [
          {
            description: t(
              'Feathr people from forms and other sources become Virtuous contacts after the next Virtuous import.',
            ),
            key: PERSON_UPSYNC_KEY,
            label: t('Create Virtuous contacts from Feathr people'),
            name: 'virtuous-person-upsync-card',
          },
        ],
      },
      {
        crmRecordLabel: t('Contact notes'),
        description: t('Write Feathr engagement to the matching Virtuous contact.'),
        heading: t('Activities'),
        key: 'activity',
        syncRule: 'up_sync',
        tab: 'activities',
        toggles: [
          {
            description: t(
              'Email opens and clicks, form submissions and ad clicks are written to the contact as one note per campaign.',
            ),
            field: {
              key: NOTE_TYPE_KEY,
              type: 'select',
              label: t('Note type'),
              placeholder: t('Search note types'),
              required: true,
            },
            key: ACTIVITY_UPSYNC_KEY,
            label: t('Sync Feathr activity to Virtuous'),
            name: 'virtuous-activity-upsync-card',
          },
        ],
      },
      {
        crmRecordLabel: t('Gifts'),
        description: t('Sync gift and donation activity with Virtuous.'),
        heading: t('Donations'),
        key: 'gifts',
        syncRule: 'both_ways',
        tab: 'activities',
        toggles: [
          {
            description: t('Virtuous gifts sync to the matching Feathr person record.'),
            key: GIFT_DOWNSYNC_KEY,
            label: t('Sync Virtuous gifts to Feathr'),
            name: 'virtuous-gift-downsync-card',
          },
          {
            description: t(
              'Feathr donations become Virtuous gifts, designated to the project you choose, after the next Virtuous import.',
            ),
            field: {
              key: PROJECT_KEY,
              type: 'select',
              label: t('Project'),
              placeholder: t('Search projects'),
              required: true,
            },
            key: DONATION_UPSYNC_KEY,
            label: t('Sync Feathr donations to Virtuous'),
            name: 'virtuous-donation-upsync-card',
          },
        ],
      },
    ],
  };
}
